import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt, EyeSlash, ShieldWarning } from "@phosphor-icons/react";

export default function PostComposer({ condition, onPosted }) {
  const [body, setBody] = useState("");
  const [tag, setTag] = useState("");
  const [tags, setTags] = useState([]);
  const [anonymous, setAnonymous] = useState(true);
  const [busy, setBusy] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const [suggested, setSuggested] = useState(null);

  const addTag = () => {
    const t = tag.trim().toLowerCase();
    if (t && !tags.includes(t) && tags.length < 5) setTags([...tags, t]);
    setTag("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    setWarnings([]); setSuggested(null);
    try {
      await api.post("/posts", { body, tags, condition: condition || null, anonymous });
      setBody(""); setTags([]);
      toast.success("Shared with the circle.");
      onPosted?.();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "safety_blocked") {
        setWarnings(d.warnings || []);
        setSuggested(d.clean_text);
        toast.error("Safety layer flagged your post, please review.");
      } else {
        toast.error("Could not post");
      }
    }
    setBusy(false);
  };

  const applySuggestion = () => { if (suggested) setBody(suggested); setWarnings([]); setSuggested(null); };

  return (
    <form onSubmit={submit} data-testid="post-composer"
          className="bg-white border border-border rounded-2xl p-5 shadow-soft">
      <textarea
        rows={3} value={body} onChange={(e) => setBody(e.target.value)}
        placeholder={condition ? `Share your ${condition} story…` : "What's on your heart today?"}
        data-testid="composer-body"
        className="w-full bg-transparent resize-none border-none focus:ring-0 focus:outline-none text-base placeholder:text-muted-foreground"
      />
      <div className="flex flex-wrap gap-2 mt-2">
        {tags.map(t => (
          <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-sage-50 text-sage-700 border border-sage-100"
                onClick={() => setTags(tags.filter(x => x !== t))}>#{t} ×</span>
        ))}
      </div>

      {warnings.length > 0 && (
        <div className="mt-3 p-3 bg-coral-50 border border-coral-100 rounded-xl" data-testid="safety-warnings">
          <div className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-coral-700 mb-2">
            <ShieldWarning size={14} weight="duotone" /> Safety layer
          </div>
          <ul className="text-sm text-ink/80 space-y-1">
            {warnings.map((w, i) => <li key={i}>· {w.message}</li>)}
          </ul>
          {suggested && (
            <button type="button" onClick={applySuggestion} data-testid="safety-apply"
              className="mt-3 text-xs bg-ink text-cream px-3 py-1.5 rounded-full hover:bg-coral transition-colors">
              Apply safe version
            </button>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 mt-3 pt-3 border-t border-border">
        <div className="flex items-center gap-2">
          <input value={tag} onChange={(e) => setTag(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                 placeholder="Add tag"
                 data-testid="composer-tag"
                 className="text-xs bg-muted rounded-full px-3 py-1.5 outline-none w-28" />
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer" data-testid="composer-anon">
            <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)}
                   className="accent-coral" />
            <EyeSlash size={14} weight="duotone" /> Anonymous
          </label>
        </div>
        <button type="submit" disabled={busy || !body.trim()}
                data-testid="composer-submit"
                className="flex items-center gap-2 bg-ink text-cream px-4 py-2 rounded-full text-sm hover:bg-coral disabled:opacity-50 transition-colors">
          <PaperPlaneTilt size={14} weight="fill" /> Share
        </button>
      </div>
    </form>
  );
}
